import type { ReadinessReport } from '@/features/generation-readiness/check-readiness'
import { fingerprintAppGenerationSpec } from '@/services/block-api-service'
import {
  promptAppPlanFromProvidedAppSpec,
  validatePartialProvidedApplicationAuthority,
  type StudioProvidedApplicationAuthority,
} from './application-authority'
import {
  resolveStudioExactGenerationAuthority,
  type StudioExactGenerationAuthority,
} from './exact-generation-authority'
import {
  STUDIO_GENERATION_DESIGN_AXES,
  type StudioAuthorityClassificationInput,
} from './generation-authority-envelope'

export type StudioGenerationEntry = 'chat' | 'wizard' | 'source-import'

export interface NormalizeStudioGenerationEntryFactsInput {
  entry: StudioGenerationEntry
  promptRef: string
  readiness: ReadinessReport
  applicationAuthority?: StudioProvidedApplicationAuthority
  exactGenerationAuthority?: StudioExactGenerationAuthority
}

export interface StudioNormalizedGenerationEntryFacts {
  entry: StudioGenerationEntry
  promptRef: string
  readiness: ReadinessReport
  applicationAuthority?: StudioProvidedApplicationAuthority
  exactGenerationAuthority?: StudioExactGenerationAuthority
}

function assertUsableReadiness(readiness: ReadinessReport): void {
  if (!readiness || typeof readiness !== 'object') {
    throw new Error('Generation entry requires readiness evidence.')
  }
  if (
    readiness.status !== 'success' ||
    readiness.blockingCount !== 0 ||
    !Array.isArray(readiness.items) ||
    !readiness.checkedAt ||
    typeof readiness.machineValidation?.performed !== 'boolean'
  ) {
    throw new Error('Generation entry requires a successful, unblocked readiness report.')
  }
  if (!readiness.designSystem) {
    throw new Error('Generation entry requires the readiness design-system fact.')
  }
}

function normalizeApplicationAuthority(
  authority: StudioProvidedApplicationAuthority | undefined,
): StudioProvidedApplicationAuthority | undefined {
  if (!authority) return undefined
  if (!authority.appSpec || typeof authority.appSpec !== 'object') {
    throw new Error('Generation entry application authority requires an AppSpec.')
  }
  if (authority.mode === 'locked-exact-spec') {
    throw new Error(
      'Locked exact application authority must be supplied through exactGenerationAuthority.',
    )
  }
  if (authority.mode === 'partial-provided-spec') {
    validatePartialProvidedApplicationAuthority(authority)
  } else {
    promptAppPlanFromProvidedAppSpec(authority.appSpec)
    if (authority.appSpecRef !== fingerprintAppGenerationSpec(authority.appSpec)) {
      throw new Error(
        'Provided application spec reference conflicts with its fingerprint.',
      )
    }
  }
  return structuredClone(authority)
}

/**
 * Normalizes only facts that every Studio generation entry can collect.
 * Authority facts owned by the canonical start path are deliberately absent:
 * this function does not read global state or invent source, design, rights,
 * subject, or claim-ceiling facts from a readiness report.
 */
export function normalizeStudioGenerationEntryFacts(
  input: NormalizeStudioGenerationEntryFactsInput,
): StudioNormalizedGenerationEntryFacts {
  if (!['chat', 'wizard', 'source-import'].includes(input.entry)) {
    throw new Error('Generation entry provenance is invalid.')
  }
  if (!input.promptRef || input.promptRef.trim() !== input.promptRef) {
    throw new Error('Generation entry requires a stable prompt reference.')
  }
  assertUsableReadiness(input.readiness)
  if (input.applicationAuthority && input.exactGenerationAuthority) {
    throw new Error(
      'Exact generation authority cannot compete with application authority.',
    )
  }

  const exactGenerationAuthority = input.exactGenerationAuthority
    ? resolveStudioExactGenerationAuthority(input.exactGenerationAuthority)
    : undefined
  const applicationAuthority = normalizeApplicationAuthority(
    input.applicationAuthority,
  )

  return {
    entry: input.entry,
    promptRef: input.promptRef,
    readiness: structuredClone(input.readiness),
    ...(applicationAuthority ? { applicationAuthority } : {}),
    ...(exactGenerationAuthority ? { exactGenerationAuthority } : {}),
  }
}

export interface BuildStudioGenerationAuthorityInput {
  commonFacts: StudioNormalizedGenerationEntryFacts
  projectId: string | null
  tenantId: string | null
  sourceSufficiency: StudioAuthorityClassificationInput['sourceSufficiency']
  designAuthority: StudioAuthorityClassificationInput['designAuthority']
  rights: StudioAuthorityClassificationInput['rights']
  questions: StudioAuthorityClassificationInput['questions']
  claimCeiling: StudioAuthorityClassificationInput['claimCeiling']
}

function stableUnique(values: readonly string[]): string[] {
  return [...new Set(values)].sort()
}

function applicationAuthorityFromCommonFacts(
  commonFacts: StudioNormalizedGenerationEntryFacts,
): StudioAuthorityClassificationInput['applicationAuthority'] {
  const authority =
    commonFacts.exactGenerationAuthority?.application ??
    commonFacts.applicationAuthority
  if (!authority) {
    return {
      mode: 'planner-candidate',
      appSpecRef: null,
      evidenceRefs: ['application:planner-candidate'],
      lockedPaths: [],
      openSlots: [],
    }
  }
  return {
    mode: authority.mode,
    appSpecRef: authority.appSpecRef,
    evidenceRefs: stableUnique([
      ...authority.evidenceRefs,
      ...(commonFacts.exactGenerationAuthority?.requiredAssetEvidenceRefs ?? []),
    ]),
    lockedPaths: stableUnique(authority.lockedPaths),
    openSlots: stableUnique(authority.openSlots),
  }
}

function exactDesignAuthority(
  exact: StudioExactGenerationAuthority,
): StudioAuthorityClassificationInput['designAuthority'] {
  const release = exact.designSystemPackRelease
  return {
    mode: 'pack-release-exact',
    authorityRef: `design-pack:${release.releaseId}`,
    evidenceRefs: stableUnique([
      release.releaseContentHash,
      release.packageHash,
    ]),
    lockedAxes: [...STUDIO_GENERATION_DESIGN_AXES],
    unresolvedAxes: [],
  }
}

function assertSourceDerivedDesignMatchesReadiness(
  commonFacts: StudioNormalizedGenerationEntryFacts,
  authority: StudioAuthorityClassificationInput['designAuthority'],
): void {
  if (authority.mode !== 'source-derived') return
  const designSystem = commonFacts.readiness.designSystem
  if (!designSystem) {
    throw new Error('Source-derived authority requires the readiness design-system fact.')
  }
  const lockedAxes = STUDIO_GENERATION_DESIGN_AXES.filter(
    (axis) => designSystem.axes[axis].origin !== 'gap',
  )
  const unresolvedAxes = STUDIO_GENERATION_DESIGN_AXES.filter(
    (axis) => designSystem.axes[axis].origin === 'gap',
  )
  if (
    JSON.stringify(stableUnique(authority.lockedAxes)) !==
      JSON.stringify(stableUnique(lockedAxes)) ||
    JSON.stringify(stableUnique(authority.unresolvedAxes)) !==
      JSON.stringify(stableUnique(unresolvedAxes))
  ) {
    throw new Error(
      'Source-derived authority conflicts with the readiness design-system fact.',
    )
  }
}

/**
 * Builds the one classifier input consumed by the canonical generation start.
 * Entry provenance never enters the returned authority facts, so equal user
 * facts remain byte-equivalent across chat, wizard, and source import.
 */
export function buildStudioGenerationAuthorityInput(
  input: BuildStudioGenerationAuthorityInput,
): StudioAuthorityClassificationInput {
  if (!input.sourceSufficiency?.schemaVersion) {
    throw new Error('Generation authority requires a source sufficiency decision.')
  }
  if (
    JSON.stringify(input.questions) !==
    JSON.stringify(input.sourceSufficiency.questions)
  ) {
    throw new Error(
      'Generation authority questions must preserve the source sufficiency decision.',
    )
  }

  const exact = input.commonFacts.exactGenerationAuthority
  const designAuthority = exact
    ? exactDesignAuthority(exact)
    : structuredClone(input.designAuthority)
  assertSourceDerivedDesignMatchesReadiness(input.commonFacts, designAuthority)

  const sourceSufficiency = structuredClone(input.sourceSufficiency)
  if (exact) {
    sourceSufficiency.evidenceRefs = stableUnique([
      ...sourceSufficiency.evidenceRefs,
      ...exact.sourceEvidenceRefs,
    ])
  }

  const rights = structuredClone(input.rights)
  if (exact) {
    rights.receiptRefs = stableUnique(exact.rightsEvidenceRefs)
  }

  return {
    promptRef: input.commonFacts.promptRef,
    projectId: input.projectId,
    tenantId: input.tenantId,
    sourceSufficiency,
    designAuthority,
    applicationAuthority: applicationAuthorityFromCommonFacts(
      input.commonFacts,
    ),
    rights,
    questions: structuredClone(input.questions),
    claimCeiling: exact
      ? {
          ...structuredClone(input.claimCeiling),
          exactVisualConformanceRequired: true,
          sourceReadbackRequired: true,
        }
      : structuredClone(input.claimCeiling),
  }
}
